import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Checkbox, FormControlLabel, Grid, TextField, Typography } from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import EditIcon from '@mui/icons-material/Edit';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { DataGrid, GridActionsCellItem, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { frFR } from '@mui/x-data-grid/locales';
import { toast } from 'react-toastify';
import { SupplierGroup, SupplierGroupPayload } from '../../../../models/supplierAdministration';
import { supplierAdministrationApi } from '../../../../services/supplierAdministrationApi';
import { CustomPaper } from '../../styles';
import ConfirmDialog from './ConfirmDialog';
import SupplierGroupDialog from './SupplierGroupDialog';

type SupplierGroupsTabProps = {
	canModify: boolean;
}

type StatusAction = {
	group: SupplierGroup;
	enable: boolean;
}

const SupplierGroupsTab = ({ canModify }: SupplierGroupsTabProps) => {
	const [rows, setRows] = useState<SupplierGroup[]>([]);
	const [total, setTotal] = useState(0);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [search, setSearch] = useState('');
	const [appliedSearch, setAppliedSearch] = useState('');
	const [includeInactive, setIncludeInactive] = useState(false);
	const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 10 });
	const [dialogGroup, setDialogGroup] = useState<SupplierGroup | null>(null);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [statusAction, setStatusAction] = useState<StatusAction | null>(null);
	const [statusLoading, setStatusLoading] = useState(false);

	const loadGroups = useCallback(() => {
		setLoading(true);
		setError(null);
		supplierAdministrationApi.listSupplierGroups({
			page: paginationModel.page + 1,
			limit: paginationModel.pageSize,
			searchTerm: appliedSearch || undefined,
			includeInactive,
		})
			.then((response) => {
				setRows(response.data);
				setTotal(response.total);
			})
			.catch((error: Error) => setError(error.message))
			.finally(() => setLoading(false));
	}, [appliedSearch, includeInactive, paginationModel.page, paginationModel.pageSize]);

	useEffect(() => {
		loadGroups();
	}, [loadGroups]);

	const submitSearch = () => {
		setPaginationModel((current) => ({ ...current, page: 0 }));
		setAppliedSearch(search.trim());
	};

	const openCreateDialog = () => {
		setDialogGroup(null);
		setDialogOpen(true);
	};

	const openEditDialog = (group: SupplierGroup) => {
		setDialogGroup(group);
		setDialogOpen(true);
	};

	const submitGroup = async (payload: SupplierGroupPayload) => {
		if (dialogGroup) {
			await supplierAdministrationApi.updateSupplierGroup(dialogGroup.id, payload);
			toast.success('Le groupe fournisseur a bien été modifié.', { position: 'bottom-center', theme: 'colored' });
		} else {
			await supplierAdministrationApi.createSupplierGroup(payload);
			toast.success('Le groupe fournisseur a bien été créé.', { position: 'bottom-center', theme: 'colored' });
		}
		loadGroups();
	};

	const confirmStatusAction = async () => {
		if (!statusAction) return;

		setStatusLoading(true);
		try {
			if (statusAction.enable) {
				await supplierAdministrationApi.enableSupplierGroup(statusAction.group.id);
				toast.success('Le groupe fournisseur a bien été réactivé.', { position: 'bottom-center', theme: 'colored' });
			} else {
				await supplierAdministrationApi.disableSupplierGroup(statusAction.group.id);
				toast.success('Le groupe fournisseur a bien été désactivé.', { position: 'bottom-center', theme: 'colored' });
			}
			setStatusAction(null);
			loadGroups();
		} catch (error: any) {
			toast.error(error?.message || 'Une erreur est survenue.', { position: 'bottom-center', theme: 'colored' });
		} finally {
			setStatusLoading(false);
		}
	};

	const columns = useMemo<GridColDef<SupplierGroup>[]>(
		() => [
			{ field: 'code', headerName: 'Code', flex: 0.4, minWidth: 100 },
			{ field: 'name', headerName: 'Nom', flex: 1, minWidth: 240 },
			{
				field: 'isActive',
				headerName: 'Statut',
				flex: 0.5,
				minWidth: 130,
				valueGetter: (_, row) => row.isActive ? 'Actif' : 'Inactif',
			},
			{
				field: 'actions',
				type: 'actions',
				headerName: 'Actions',
				flex: 0.5,
				minWidth: 120,
				getActions: ({ row }) => [
					<GridActionsCellItem
						key="edit"
						icon={<EditIcon />}
						label="Modifier"
						disabled={!canModify}
						onClick={() => openEditDialog(row)}
					/>,
					row.isActive ? (
						<GridActionsCellItem
							key="disable"
							icon={<BlockIcon />}
							label="Désactiver"
							disabled={!canModify}
							onClick={() => setStatusAction({ group: row, enable: false })}
						/>
					) : (
						<GridActionsCellItem
							key="enable"
							icon={<RestartAltIcon />}
							label="Réactiver"
							disabled={!canModify}
							onClick={() => setStatusAction({ group: row, enable: true })}
						/>
					),
				],
			},
		],
		[canModify]
	);

	return (
		<CustomPaper>
			<Grid container spacing={2} sx={{ alignItems: 'center', mb: 2 }}>
				<Grid size={{ xs: 12, md: 4 }}>
					<Typography variant="h5" color="primary">Groupes fournisseurs</Typography>
				</Grid>
				<Grid size={{ xs: 12, md: 3 }}>
					<TextField
						label="Recherche"
						value={search}
						onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSearch(event.target.value)}
						onKeyDown={(event) => {
							if (event.key === 'Enter') submitSearch();
						}}
						fullWidth
						size="small"
					/>
				</Grid>
				<Grid size={{ xs: 12, md: 2 }}>
					<FormControlLabel
						control={
							<Checkbox
								checked={includeInactive}
								onChange={(event) => {
									setIncludeInactive(event.target.checked);
									setPaginationModel((current) => ({ ...current, page: 0 }));
								}}
							/>
						}
						label="Inclure les inactifs"
					/>
				</Grid>
				<Grid size={{ xs: 12, md: 3 }}>
					<Box display="flex" gap={1} justifyContent="flex-end">
						<Button variant="contained" onClick={submitSearch}>Rechercher</Button>
						<Button variant="contained" color="success" disabled={!canModify} onClick={openCreateDialog}>Créer</Button>
					</Box>
				</Grid>
			</Grid>

			{error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
			<DataGrid
				rows={rows}
				columns={columns}
				loading={loading}
				rowCount={total}
				paginationMode="server"
				paginationModel={paginationModel}
				onPaginationModelChange={setPaginationModel}
				pageSizeOptions={[10, 25, 50]}
				disableRowSelectionOnClick
				localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
				autoHeight
			/>

			<SupplierGroupDialog
				open={dialogOpen}
				group={dialogGroup}
				onClose={() => setDialogOpen(false)}
				onSubmit={submitGroup}
			/>
			<ConfirmDialog
				open={!!statusAction}
				title={statusAction?.enable ? 'Réactiver le groupe fournisseur' : 'Désactiver le groupe fournisseur'}
				description={
					statusAction?.enable
						? `Réactiver le groupe ${statusAction.group.code} - ${statusAction.group.name} ? Les fournisseurs restent dans leur état actuel.`
						: `Désactiver le groupe ${statusAction?.group.code} - ${statusAction?.group.name} ? Les fournisseurs actifs de ce groupe seront aussi désactivés.`
				}
				confirmLabel={statusAction?.enable ? 'Réactiver' : 'Désactiver'}
				confirmColor={statusAction?.enable ? 'success' : 'error'}
				loading={statusLoading}
				onClose={() => setStatusAction(null)}
				onConfirm={confirmStatusAction}
			/>
		</CustomPaper>
	);
};

export default SupplierGroupsTab;
